import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataTable from "../../components/DataTable";
import {
  getCodeColumns,
  codeSchema,
  getCodeInitialFormValues,
} from "./codeDefaults";
import { createServiceActions } from "../../reducers/GenericActions";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";

const INITIAL_SORT_MODEL = [{ field: "code", sort: "asc" }];

const CodeTable = ({
  serviceName: propServiceName,
  stateName: propStateName,
  tableName: propTableName,
  columnOrderKey: propColumnOrderKey,
  columnSizeKey: propColumnSizeKey,
  columnVisibilityKey: propColumnVisibilityKey,
  tableSortModelKey: propTableSortModelKey,
  title: propTitle,
}) => {
  const userAgency = useSelector((state) => state.user.userAgency);
  const location = useLocation();
  const history = useHistory();

  const [serviceName, setServiceName] = useState(propServiceName);
  const [stateName, setStateName] = useState(propStateName);
  const [tableName, setTableName] = useState(propTableName);
  const [columnOrderKey, setColumnOrderKey] = useState(propColumnOrderKey);
  const [columnSizeKey, setColumnSizeKey] = useState(propColumnSizeKey);
  const [columnVisibilityKey, setColumnVisibilityKey] = useState(
    propColumnVisibilityKey
  );
  const [tableSortModelKey, setTableSortModelKey] = useState(
    propTableSortModelKey
  );
  const [title, setTitle] = useState(propTitle);

  useEffect(() => {
    if (location.state) {
      const route = location.state;

      if (route.serviceName) setServiceName(route.serviceName);
      if (route.stateName) setStateName(route.stateName);
      if (route.tableName) setTableName(route.tableName);
      if (route.columnOrderKey) setColumnOrderKey(route.columnOrderKey);
      if (route.columnSizeKey) setColumnSizeKey(route.columnSizeKey);
      if (route.columnVisibilityKey)
        setColumnVisibilityKey(route.columnVisibilityKey);
      if (route.tableSortModelKey)
        setTableSortModelKey(route.tableSortModelKey);
      if (route.title) setTitle(route.title);

      history.replace({
        pathname: location.pathname,
        state: null,
      });
    }
  }, [location.state, history, location.pathname]);

  const serviceActions = useMemo(() => {
    if (!serviceName || !tableName) return {};

    return createServiceActions(serviceName, tableName, userAgency);
  }, [serviceName, tableName, userAgency]);

  const { fetchData, createData, updateData, deleteData } = serviceActions;

  const dispatch = useDispatch();
  const { data, loading, total } = useSelector((state) => state[stateName] || { data: [], loading: false, total: 0 });
  const themeMode = useSelector((state) => state.theme.mode);
  const { userData } = useSelector((state) => state.user);
  const { userSettings } = useSelector((state) => state.userSettings);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState("");
  const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
  const [columnsOrder, setColumnsOrder] = useState([]);
  const [columnsSize, setColumnsSize] = useState({});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [codeColumns, setCodeColumns] = useState([]);
  const [codeInitialFormValues, setCodeInitialFormValues] = useState({});
  

  useEffect(() => {
    setCodeColumns(getCodeColumns(title));
    setCodeInitialFormValues(getCodeInitialFormValues(title));
  }, [title]);

  const columns = useMemo(() => {
    const hasOrder = columnsOrder.length > 0;
    const hasSizes = Object.keys(columnsSize).length > 0;
    if (!hasOrder && !hasSizes) return codeColumns;

    let baseColumns;

    if (hasOrder) {
      const ordered = columnsOrder
        .map((field) => codeColumns.find((col) => col.field === field))
        .filter(Boolean);

      const remaining = codeColumns.filter(
        (col) => !columnsOrder.includes(col.field)
      );

      const actionsIndex = ordered.findIndex((col) => col.field === "actions");
      const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

      baseColumns = [
        ...ordered.slice(0, insertAt),
        ...remaining,
        ...ordered.slice(insertAt),
      ];
    } else {
      baseColumns = codeColumns;
    }

    const finalColumns = baseColumns.map((col) => ({
      ...col,
      width: columnsSize[col.field] ?? col.width ?? 100,
    }));

    return finalColumns;
  }, [columnsOrder, columnsSize, codeColumns]);

  useEffect(() => {
    if (settingsLoaded) return;
    const settingsMap = Object.fromEntries(
      userSettings.map((s) => [s.key, s.value])
    );
    if (columnOrderKey && settingsMap[columnOrderKey]) {
      setColumnsOrder(JSON.parse(settingsMap[columnOrderKey]));
    } else {
      setColumnsOrder([]);
    }

    if (columnSizeKey && settingsMap[columnSizeKey]) {
      const widthArray = JSON.parse(settingsMap[columnSizeKey]);
      const result = widthArray.reduce((acc, curr) => {
        acc[curr.field] = curr.width;
        return acc;
      }, {});
      setColumnsSize(result);
    } else {
      setColumnsSize({});
    }

    if (columnVisibilityKey && settingsMap[columnVisibilityKey]) {
      setColumnVisibilityModel(JSON.parse(settingsMap[columnVisibilityKey]));
    }

    if (settingsMap[tableSortModelKey]) {
      const savedSortModel = JSON.parse(settingsMap[tableSortModelKey]);
      if (Array.isArray(savedSortModel)) {
        const validSortModel = savedSortModel.filter((model) =>
          columns.some((col) => col.field === model.field)
        );
        if (validSortModel.length > 0) {
          setSortModel(savedSortModel);
        }
      }
    }

    setSettingsLoaded(true);
  }, [
    userSettings,
    columnOrderKey,
    columnSizeKey,
    columnVisibilityKey,
    tableSortModelKey,
    settingsLoaded,
    columns,
  ]);

  useEffect(() => {
    setPage(0);
    setPageSize(20);
    const allColumnFields = columns.map((col) => col.field);
    const defaultVisibility = {};
    allColumnFields.forEach((field) => {
      defaultVisibility[field] = true;
    });
    setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
  }, [serviceName, columns]);

  useEffect(() => {
    if (!settingsLoaded) return;
    const sortField = sortModel[0]?.field || "code";
    const sortDirection = sortModel[0]?.sort || "asc";
    dispatch(fetchData(filters, page, pageSize, sortField, sortDirection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    page,
    pageSize,
    sortModel,
    serviceName,
    settingsLoaded,
    fetchData,
    filters,
  ]);

  const handleAdd = async (data) => {
    const finalData = {
      ...data,
      agencyId: userAgency,
    };

    await dispatch(createData(finalData));
  };

  const handleEdit = async (data) => {
    await dispatch(updateData(data.ptsCodeId, data));
  };

  const handleDelete = async (id) => {
    const userId = userData?.user?.ptsUserID;
    await dispatch(deleteData(id, userId));
  };

  const handleColumnOrderChange = (params, event, details) => {
    const newColumnOrder = details.api.getAllColumns().map((col) => col.field);
    if (userData?.user?.ptsUserID) {
      dispatch(
        updateOrCreateUserSetting(
          userData.user.ptsUserID,
          columnOrderKey,
          JSON.stringify(newColumnOrder)
        )
      );
    }
  };

  const handleColumnWidthChange = (params, event, details) => {
    const columnSizes = details.api.getAllColumns().map((col) => {
      return {
        field: col.field,
        width: col.width,
      };
    });
    if (userData?.user?.ptsUserID) {
      dispatch(
        updateOrCreateUserSetting(
          userData.user.ptsUserID,
          columnSizeKey,
          JSON.stringify(columnSizes)
        )
      );
    }
  };

  const handleColumnVisibilityModelChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    if (userData?.user?.ptsUserID) {
      dispatch(
        updateOrCreateUserSetting(
          userData.user.ptsUserID,
          columnVisibilityKey,
          JSON.stringify(newModel)
        )
      );
    }
  };

  const handleSortModelChange = useCallback(
    (model) => {
      const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

      // Avoid unnecessary state updates if sort model didn't change
      const isSortModelDifferent =
        JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

      if (isSortModelDifferent) {
        setSortModel(newSortModel);
        setPage(0);

        if (userData?.user?.ptsUserID) {
          dispatch(
            updateOrCreateUserSetting(
              userData.user.ptsUserID,
              tableSortModelKey,
              JSON.stringify(newSortModel)
            )
          );
        }
      }
    },
    [dispatch, userData?.user?.ptsUserID, sortModel, tableSortModelKey]
  );

  return (
    <DataTable
      key={`${columnsOrder.join(",")}-${Object.values(columnsSize).join(",")}`}
      data={data}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      total={total}
      loading={loading}
      schema={codeSchema}
      initialFormValues={codeInitialFormValues}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      idField={"ptsCodeId"}
      themeMode={themeMode}
      page={page}
      pageSize={pageSize}
      sortModel={sortModel}
      setPage={setPage}
      setPageSize={setPageSize}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnWidthChange={handleColumnWidthChange}
      onColumnVisibilityChange={handleColumnVisibilityModelChange}
      onSortModelChange={handleSortModelChange}
      setSortModel={setSortModel}
      serviceName={serviceName}
      title={title}
      onFilterChange={setFilters}
    />
  );
};

export default CodeTable;
